import {
  AfterViewInit,
  ChangeDetectorRef,
  ComponentRef,
  Directive,
  ElementRef,
  inject,
  Injectable,
  input,
  NgZone,
  OnDestroy,
  output,
  TemplateRef,
} from '@angular/core';
import {
  CdkScrollable,
  ConnectedPosition,
  Overlay,
  OverlayContainer,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { TooltipCommonType, TooltipComponent } from '../components/tooltip/tooltip.component';
import { distinctUntilChanged, fromEvent, map, merge, Subject, take, takeUntil, tap } from 'rxjs';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { ComponentPortal } from '@angular/cdk/portal';
import { outputToObservable } from '@angular/core/rxjs-interop';

export abstract class TooltipPositions {
  static readonly TOP: TooltipPosition = {
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'bottom',
    offsetY: -6,
  };

  static readonly RIGHT: TooltipPosition = {
    originX: 'end',
    originY: 'center',
    overlayX: 'start',
    overlayY: 'center',
    offsetX: 6,
  };

  static readonly LEFT: TooltipPosition = {
    originX: 'start',
    originY: 'center',
    overlayX: 'end',
    overlayY: 'center',
    offsetX: -6,
  };

  static readonly OVER_START: TooltipPosition = {
    originX: 'start',
    originY: 'top',
    overlayX: 'start',
    overlayY: 'top',
  };

  static readonly OVER_END: TooltipPosition = {
    originX: 'end',
    originY: 'top',
    overlayX: 'end',
    overlayY: 'top',
  };

  static readonly BOTTOM: TooltipPosition = {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: 6,
  };
}

export type TooltipPosition = ConnectedPosition;
export type TooltipOptions = {
  trigger: 'hover' | 'click';
  hideDelay?: number;
};

@Directive({
  selector: '[cueTooltip]',
  standalone: true,
})
export class TooltipDirective<TContext> implements AfterViewInit, OnDestroy {
  cdr = inject(ChangeDetectorRef);
  zone = inject(NgZone);
  elRef = inject(ElementRef);
  overlay = inject(Overlay);
  overlayPositionBuilder = inject(OverlayPositionBuilder);
  terminator = injectDestroy();

  cueTooltiptOptions = input<TooltipOptions>({
    trigger: 'hover',
  });

  cueTooltipScrollable = input<CdkScrollable | undefined>();
  cueTooltipScrollStrategy = input<'reposition' | 'close'>('reposition');
  cueTooltipCommonType = input<TooltipCommonType | undefined>();
  cueTooltipContext = input<TContext | undefined>();
  cueTooltip = input<string | TemplateRef<TContext> | undefined>();
  cueTooltipPosition = input<TooltipPosition | TooltipPosition[]>(TooltipPositions.TOP);
  cueTooltipPanelClass = input<string | string[] | undefined>();
  cueTooltipHasBackdrop = input<boolean>(false);
  cueTooltipBackdropClass = input<string | string[] | undefined>();

  cueTooltipOpened = output();
  cueTooltipClosed = output();

  __overlayRef?: OverlayRef;
  _overlayRefComponentInstance: ComponentRef<TooltipComponent<TContext>> | undefined;

  // Logic of hiding
  _hideTimeout?: ReturnType<typeof setTimeout>;
  _hideOb$?: Subject<void>;
  _blured$ = new Subject<boolean>();
  __clear = new Subject<void>();

  clearHideTimeout() {
    if (this._hideTimeout) {
      clearTimeout(this._hideTimeout);
    }
  }

  setHideTimeout() {
    this.clearHideTimeout();
    this._hideTimeout = setTimeout(() => {
      this._hideOb$!.next();
    }, this.cueTooltiptOptions().hideDelay || 100);
  }

  getStartObs() {
    if (this.cueTooltiptOptions().trigger === 'hover') {
      this._hideOb$ = new Subject<void>();
      fromEvent(this.elRef.nativeElement, 'mouseleave')
        .pipe(takeUntil(this.terminator))
        .subscribe(() => {
          this.setHideTimeout();
        });
      return fromEvent(this.elRef.nativeElement, 'mouseenter').pipe(
        tap(() => this.clearHideTimeout()),
        takeUntil(this.terminator),
        map(() => true)
      );
    } else {
      return fromEvent(this.elRef.nativeElement, 'click').pipe(
        takeUntil(this.terminator),
        map(() => true)
      );
    }
  }

  getEndObs() {
    if (this.cueTooltiptOptions().trigger === 'hover') {
      return this._hideOb$!.pipe(map(() => false));
    } else {
      return this._blured$.pipe(map(() => false));
    }
  }

  get overlayRef() {
    if (this.elRef && !this.__overlayRef) {
      let position = this.overlayPositionBuilder
        .flexibleConnectedTo(this.elRef.nativeElement)
        .withViewportMargin(16)
        .withPositions(
          Array.isArray(this.cueTooltipPosition()) ? (this.cueTooltipPosition() as any) : [this.cueTooltipPosition()]
        );

      if (this.cueTooltipScrollable) {
        position = position
          .withFlexibleDimensions(false)
          .withPush(true)
          .withScrollableContainers([this.cueTooltipScrollable()]);
      }

      this.__overlayRef = this.overlay.create({
        positionStrategy: position,
        scrollStrategy:
          this.cueTooltipScrollStrategy() === 'reposition'
            ? this.overlay.scrollStrategies.reposition()
            : this.overlay.scrollStrategies.close(),
        panelClass: this.cueTooltipPanelClass(),
        hasBackdrop: this.cueTooltipHasBackdrop(),
        backdropClass: this.cueTooltipBackdropClass(),
      });

      if (this.cueTooltipHasBackdrop()) {
        this.__overlayRef
          .backdropClick()
          .pipe(
            takeUntil(this.__clear),
            tap(() => this._blured$.next(true)),
            takeUntil(this.terminator)
          )
          .subscribe();

        fromEvent(this.__overlayRef.overlayElement, 'click')
          .pipe(
            takeUntil(this.__clear),
            tap(() => this._blured$.next(true)),
            takeUntil(this.terminator)
          )
          .subscribe();
      }

      if (this.cueTooltiptOptions().trigger === 'hover') {
        fromEvent(this.__overlayRef.overlayElement, 'mouseleave')
          .pipe(
            takeUntil(this.__clear),
            tap(() => this.setHideTimeout()),
            takeUntil(this.terminator)
          )
          .subscribe();

        fromEvent(this.__overlayRef.overlayElement, 'mouseenter')
          .pipe(
            takeUntil(this.__clear),
            tap(() => this.clearHideTimeout()),
            takeUntil(this.terminator)
          )
          .subscribe();
      }
    }

    return this.__overlayRef;
  }

  ngOnDestroy(): void {
    this.hideTooltip();
    this.clearHideTimeout();
  }

  showTooltip() {
    this.zone.run(() => {
      if (this.cueTooltip() === undefined || this.cueTooltip() === null || this.cueTooltip() === '') {
        return;
      }
      const tooltipPortal = new ComponentPortal(TooltipComponent<TContext>);
      this._overlayRefComponentInstance = this.overlayRef!.attach(tooltipPortal);

      this._overlayRefComponentInstance.setInput('commonType', this.cueTooltipCommonType());
      if (Object.prototype.toString.call(this.cueTooltip()) === '[object String]') {
        this._overlayRefComponentInstance.setInput('text', this.cueTooltip());
      } else {
        this._overlayRefComponentInstance.setInput('template', this.cueTooltip());
        if (this.cueTooltipContext() != undefined) {
          this._overlayRefComponentInstance.setInput('templateContext', this.cueTooltipContext());
        }
      }

      if (this.cueTooltiptOptions().trigger === 'click') {
        outputToObservable(this._overlayRefComponentInstance.instance.tooltipBlur)
          .pipe(
            takeUntil(this.__clear),
            tap(() => this._blured$.next(true)),
            take(1),
            takeUntil(this.terminator)
          )
          .subscribe();
      }
      this.cueTooltipOpened.emit();
    });
  }

  hideTooltip() {
    this._overlayRefComponentInstance = undefined;
    this.overlayRef?.detach();
    this.overlayRef?.dispose();
    this.__overlayRef = undefined;
    this.cueTooltipClosed.emit();
    this.__clear.next();
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      const starObservable = this.getStartObs();
      const endObservable = this.getEndObs();

      merge(starObservable, endObservable)
        .pipe(
          distinctUntilChanged(),
          tap((show) => {
            if (show && !(this.cueTooltip() === undefined || this.cueTooltip() === null || this.cueTooltip() === '')) {
              this.showTooltip();
              this.overlayRef!.updatePosition();
              this._overlayRefComponentInstance?.changeDetectorRef.detectChanges();
            } else {
              this.hideTooltip();
            }
            this.cdr.detectChanges();
          }),
          takeUntil(this.terminator)
        )
        .subscribe();
    });
  }
}
